import React, { useEffect } from 'react'

import {
  addEmptySelection,
  removeSelection,
  updateSelectionItems,
} from '@disco/state-management/actions/SelectionActions'
import { getAllSelection } from '@disco/state-management/selectors/selection'

import { useDispatch, useSelector } from 'hooks/use-redux'

export type SelectionContextType = {
  id: string | null
  paneIndex: number | null
}
export const SelectionContext: React.Context<SelectionContextType> =
  React.createContext<SelectionContextType>({
    id: null,
    paneIndex: null,
  })
type Props = {
  children: React.ReactNode
  value: {
    id: string
    data: Record<string, any>[]
    paneIndex: number | null
  }
  location?: 'shortlist'
}

const getExplorerSelectionItems = (data: Record<string, any>[]) => {
  return data.map(item => ({
    type: item.type,
    id: item.playlist_id || item.id,
  }))
}

const getShortlistSelectionItems = (data: Record<string, any>[]) => {
  return data
    .map(item => {
      if (item.track) {
        return {
          id: item.tempId || item.track.id,
          type: item.track.type,
        }
      }
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
}

const getSelectionItems = (
  data: Record<string, any>[],
  location?: 'shortlist',
) => {
  switch (location) {
    case 'shortlist':
      return getShortlistSelectionItems(data)

    default:
      return getExplorerSelectionItems(data)
  }
}

export const SelectionProvider = ({
  children,
  value,
  location,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const selections = useSelector(getAllSelection)
  const selection = selections?.[value.id]
  const selectionItems = getSelectionItems(value.data, location)
  useEffect(() => {
    return () => {
      dispatch(removeSelection(value.id))
    }
  }, [])
  useEffect(() => {
    const selectionProps = {
      selectionId: value.id,
      selectionItems,
      paneIndex: value.paneIndex,
    }

    if (selection) {
      // update selectionItems when adding/removing items
      dispatch(updateSelectionItems(selectionProps))
    } else {
      dispatch(addEmptySelection(selectionProps))
    }
  }, [JSON.stringify(selectionItems)])
  return (
    <SelectionContext.Provider
      value={{
        id: value.id,
        paneIndex: value.paneIndex,
      }}
    >
      {children}
    </SelectionContext.Provider>
  )
}
